<template>
  <div>
    <v-toolbar dense tabs color="primary darken-2" dark class="page_title">
      <v-toolbar-title class="pa-0 pr-0 mt-1">
        <v-layout row>
          <v-flex xs10 sm11 text-xs-left>
            <a href="javascript: history.go(-1)" class="pa-0">
              <v-btn flat fab small class="pa-0">
                <i class="fas fa-arrow-left headline"></i>
              </v-btn>
            </a>
            Business Suite
          </v-flex>
          <v-flex xs2 sm1 text-xs-right>
            <router-link :to="$store.state.close_url2" class="pa-0 text-xs-right">
              <v-btn flat fab small class="pa-0">
                <i class="fas fa-close headline"></i>
              </v-btn>
            </router-link>
          </v-flex>
        </v-layout>
      </v-toolbar-title>
    </v-toolbar>

    <div class="dashboard-container">
      <div class="summary-card primary-bg">
        <div class="summary-header">
          <h2 class="summary-title">
            <b class="notranslate no-wrap" translate="no">{{ username }}</b>
            <span>Account Summary</span>
          </h2>
          <i class="material-icons">account_balance_wallet</i>
        </div>
        <div class="summary-content">
          <div class="balance">{{ formatCurrency(userBalance) }}</div>
          <div class="pending">Pending Commissions: {{ formatCurrency(pendingCommissions) }}</div>
        </div>
      </div>

      <div class="action-buttons">
        <router-link class="btn success-bg" :to="{ name: 'dashboard.AddUser' }">
          <i class="material-icons">person_add</i>
          <span>Add User</span>
        </router-link>
        <router-link class="btn primary-bg" :to="{ name: 'dashboard.WithdrawHistory' }">
          <i class="material-icons">attach_money</i>
          <span>Referred Users</span>
        </router-link>
        <router-link class="btn warning-bg" :to="{ name: 'dashboard.DepositHistory' }">
          <i class="material-icons">money_off</i>
          <span>Account details</span>
        </router-link>
      </div>

      <div class="data-grid">
        <div class="data-section">
          <div class="card">
            <div class="card-header secondary-bg">
              <i class="material-icons">group</i>
              <h3 class="section-title">
                <span>Referred Users</span>
                <span class="count">({{ affiliateCount }})</span>
              </h3>
            </div>
            <div class="custom-table-container">
              <table class="custom-table">
                <thead>
                  <tr>
                    <th v-for="header in userHeaders" :key="header.value" class="header-cell">{{ header.text }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in paginatedUsers" :key="item.id">
                    <td class="notranslate no-wrap" translate="no">{{ item.username }}</td>
                    <td class="no-wrap">{{ formatDate(item.created_at) }}</td>
                    <td class="status-cell">
                      <i
                        class="material-icons status-icon"
                        :style="{ color: item.payment_status ? '#4CAF50' : '#F44336' }"
                      >
                        {{ item.payment_status ? 'check_circle' : 'cancel' }}
                      </i>
                    </td>
                    <td class="no-wrap amount-cell" translate="no">{{ item.payment_amount }}</td>
                  </tr>
                </tbody>
              </table>

              <div class="pagination-controls">
                <button @click="currentPage--" :disabled="currentPage === 1">Previous</button>
                <span>Page {{ currentPage }} of {{ totalPages }}</span>
                <button @click="currentPage++" :disabled="currentPage === totalPages">Next</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data: () => ({
    userBalance: 0,
    pendingCommissions: 0,
    affiliateCount: 0,
    referredUsers: [],
    username: '',
    currentPage: 1,
    itemsPerPage: 5,
    userHeaders: [
      { text: 'Username', value: 'username' },
      { text: 'Signup Date', value: 'created_at' },
      { text: 'Payment Status', value: 'payment_status' },
      { text: 'Amount', value: 'payment_amount' }
    ]
  }),

  computed: {
    totalPages() {
      return Math.ceil(this.referredUsers.length / this.itemsPerPage) || 1
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      return this.referredUsers.slice(start, start + this.itemsPerPage)
    }
  },

  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XAF'
      }).format(value || 0)
    },

    formatDate(dateString) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return new Date(dateString).toLocaleDateString('en-US', options)
    },

    async fetchDashboardData() {
      try {
        const response = await axios.get('/api/v1/home/dashboard/')
        const data = response.data || {}

        this.userBalance = data.balance || 0
        this.pendingCommissions = data.pending_commissions || 0
        this.affiliateCount = data.affiliate_count || 0
        this.username = data.username || ''

        this.referredUsers = (data.referred_users || []).map(user => ({
          id: user.id,
          username: user.username,
          created_at: user.created_at,
          payment_status: user.payment_status,
          payment_amount: user.payment_amount,
          serial_no: user.serial_no
        }))
      } catch (error) {
        console.error('Loading error:', error)
        this.$store.commit('setSnackBarMessage', error.response?.data?.message || 'Failed to load dashboard data')
        this.$store.commit('setSnackBarColor', 'red darken-2')
        this.$store.commit('activateSnackBar', true)
      }
    }
  },

  mounted() {
    this.fetchDashboardData()
  }
}
</script>

<style scoped>
.dashboard-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  overflow-x: hidden;
  box-sizing: border-box;
}

.summary-card {
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.primary-bg {
  background: #00479b;
  color: white;
}

.secondary-bg {
  background: #424242;
  color: white;
}

.success-bg {
  background: #4caf50;
}

.warning-bg {
  background: #ff9800;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  gap: 10px;
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin: 0;
}

.summary-title span {
  overflow-wrap: anywhere;
}

.balance {
  font-size: 2.5rem;
  font-weight: bold;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 24px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: white;
  gap: 8px;
  transition: opacity 0.3s;
  flex: 1 1 180px;
  min-width: 160px;
  text-align: center;
}

.btn:hover {
  opacity: 0.9;
}

.data-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 8px;
}

.section-title {
  margin: 0;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  line-height: 1.2;
}

.section-title .count {
  white-space: nowrap;
}

.custom-table-container {
  width: 100%;
  max-width: 100%;
  padding: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  table-layout: fixed;
}

.custom-table th,
.custom-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.custom-table thead th {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.25;
}

.custom-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.header-cell {
  white-space: nowrap;
}

.status-cell {
  width: 100px;
}

.status-icon {
  font-size: 24px;
  vertical-align: middle;
}

.amount-cell {
  font-variant-numeric: tabular-nums;
}

.no-wrap {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #eee;
  margin-top: 16px;
  gap: 12px;
}

button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background: #00479b;
  color: white;
  cursor: pointer;
}

button:disabled {
  background: #bbdefb;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .dashboard-container {
    padding: 12px;
  }

  .custom-table th,
  .custom-table td {
    padding: 8px;
    font-size: 0.9rem;
  }

  .btn {
    padding: 8px 12px;
    font-size: 0.9rem;
    min-width: 130px;
  }
}
</style>
